import { useEffect, useRef, useCallback } from "react";
import {
  createPollingSubscription,
  DATA_CHANGE_CHANNEL,
  DATA_CHANGE_EVENT,
} from "@/lib/apiClient";

// Reducido de 10s a 5s para actualizaciones más rápidas
export const DEFAULT_POLL_INTERVAL_MS = 5000;

// Función para forzar actualización global de datos
export function triggerGlobalRefresh() {
  window.dispatchEvent(new CustomEvent(DATA_CHANGE_EVENT));
  if (typeof BroadcastChannel !== "undefined") {
    const channel = new BroadcastChannel(DATA_CHANGE_CHANNEL);
    channel.postMessage({ type: "refresh", timestamp: Date.now() });
    channel.close();
  }
}

interface DataRefreshOptions {
  pollIntervalMs?: number;
}

export function useDataRefresh(callback: () => void, options?: DataRefreshOptions) {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Función para refrescar manualmente
  const refresh = useCallback(() => {
    callbackRef.current();
  }, []);

  useEffect(() => {
    const handleChange = () => {
      callbackRef.current();
    };

    window.addEventListener(DATA_CHANGE_EVENT, handleChange);

    let channel: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== "undefined") {
      channel = new BroadcastChannel(DATA_CHANGE_CHANNEL);
      channel.onmessage = handleChange;
    }

    return () => {
      window.removeEventListener(DATA_CHANGE_EVENT, handleChange);
      if (channel) {
        channel.close();
      }
    };
  }, []);

  useEffect(() => {
    if (!options?.pollIntervalMs) return;
    const subscription = createPollingSubscription(
      () => {
        if (typeof document !== "undefined" && document.hidden) return;
        callbackRef.current();
      },
      options.pollIntervalMs
    );
    return () => subscription.unsubscribe();
  }, [options?.pollIntervalMs]);

  return { refresh };
}
